import { dictionaryApi } from './dictionaryClient';
import { ARTHA_BIDHA } from '../constants/arthaEnums';
import { SUBANTA_SHRENI } from '../constants/subantaEnums';
import type { Language } from './languageApi';

export type ArthaBidha = (typeof ARTHA_BIDHA)[number];
/** Same enum as subanta `shreni`. */
export type ArthaPrakar = (typeof SUBANTA_SHRENI)[number];

export type Artha = {
  id: number;
  subantaId: number;
  languageId: number;
  text: string;
  vyutpatti?: string | null;
  example?: string | null;
  antonyms?: string | null;
  synonyms?: string | null;
  tags?: string | null;
  bidha: ArthaBidha;
  prakar: ArthaPrakar;
  createdAt?: string;
  updatedAt?: string;
  created_at?: string;
  updated_at?: string;
  language?: Language | null;
};

export type CreateArthaBody = {
  subantaId: number;
  languageId: number;
  text: string;
  vyutpatti?: string | null;
  example?: string | null;
  antonyms?: string | null;
  synonyms?: string | null;
  tags?: string | null;
  bidha: ArthaBidha;
  prakar: ArthaPrakar;
};

export type PatchArthaBody = Partial<
  Omit<CreateArthaBody, 'subantaId'> & { subantaId?: number }
>;

function normalizeList(data: unknown): Artha[] {
  if (Array.isArray(data)) return data as Artha[];
  if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: Artha[] }).data;
  }
  return [];
}

export function isArtha(x: unknown): x is Artha {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.id === 'number' &&
    typeof o.subantaId === 'number' &&
    typeof o.languageId === 'number' &&
    typeof o.text === 'string' &&
    typeof o.bidha === 'string' &&
    typeof o.prakar === 'string'
  );
}

export async function listArthas(): Promise<Artha[]> {
  const res = await dictionaryApi.get<unknown>('/artha');
  return normalizeList(res.data);
}

export async function getArtha(id: number): Promise<Artha> {
  const res = await dictionaryApi.get<Artha>(`/artha/${id}`);
  return res.data;
}

export async function createArtha(body: CreateArthaBody): Promise<Artha> {
  const res = await dictionaryApi.post<Artha>('/artha', body);
  return res.data;
}

export async function updateArtha(id: number, body: PatchArthaBody): Promise<Artha> {
  const res = await dictionaryApi.patch<Artha>(`/artha/${id}`, body);
  return res.data;
}

export async function deleteArtha(id: number): Promise<void> {
  await dictionaryApi.delete(`/artha/${id}`);
}

function normOpt(s: string | null | undefined): string | null {
  if (s == null) return null;
  const t = String(s).trim();
  return t === '' ? null : t;
}

/** Build a PATCH body from form values; only keys that differ from `prev` are included. */
export function buildArthaPatch(prev: Artha, next: CreateArthaBody): PatchArthaBody {
  const patch: PatchArthaBody = {};
  if (prev.languageId !== next.languageId) patch.languageId = next.languageId;
  if (prev.text !== next.text) patch.text = next.text;
  const pv = normOpt(prev.vyutpatti);
  const nv = normOpt(next.vyutpatti);
  if (pv !== nv) patch.vyutpatti = nv;
  const pe = normOpt(prev.example);
  const ne = normOpt(next.example);
  if (pe !== ne) patch.example = ne;
  const pa = normOpt(prev.antonyms);
  const na = normOpt(next.antonyms);
  if (pa !== na) patch.antonyms = na;
  const ps = normOpt(prev.synonyms);
  const ns = normOpt(next.synonyms);
  if (ps !== ns) patch.synonyms = ns;
  const pt = normOpt(prev.tags);
  const nt = normOpt(next.tags);
  if (pt !== nt) patch.tags = nt;
  if (prev.bidha !== next.bidha) patch.bidha = next.bidha;
  if (prev.prakar !== next.prakar) patch.prakar = next.prakar;
  return patch;
}

/** Map modal form values to a create body (optional strings → null when empty). */
export function arthaFormToCreateBody(
  subantaId: number,
  values: Omit<CreateArthaBody, 'subantaId'>
): CreateArthaBody {
  return {
    subantaId,
    languageId: values.languageId,
    text: values.text.trim(),
    vyutpatti: normOpt(values.vyutpatti),
    example: normOpt(values.example),
    antonyms: normOpt(values.antonyms),
    synonyms: normOpt(values.synonyms),
    tags: normOpt(values.tags),
    bidha: values.bidha,
    prakar: values.prakar,
  };
}
